import React from "react";
import { Heart, Plane, Megaphone, Code, DollarSign, ArrowUpRight, LayoutDashboard, Tv, CreditCard } from "lucide-react";

const DivisionsSection = ({ isVisible }) => {
  const divisions = [
    {
      id: "nonprofit",
      name: "Prestigious Travel Club Foundation",
      category: "Nonprofit",
      description: "A registered nonprofit dedicated to helping families, veterans, and individuals in need through community-driven outreach.",
      link: "https://prestigioustravelclub.org/",
      image: "https://images.unsplash.com/photo-1720796921866-e4fcd423e3bd?w=800",
      icon: Heart,
      features: ["Housing Support", "Food Assistance", "Veteran Care"],
      color: "bg-rose-50",
      iconBg: "bg-rose-500",
    },
    {
      id: "travel",
      name: "Prestigious Travel Club",
      category: "Travel",
      description: "Our for-profit division helps families enjoy exclusive travel experiences, personal development opportunities, and lifestyle benefits.",
      link: "https://prestigioustravelclub.net/",
      image: "https://images.pexels.com/photos/3601422/pexels-photo-3601422.jpeg?auto=compress&cs=tinysrgb&w=800",
      icon: Plane,
      features: ["Exclusive Experiences", "Personal Growth", "Family Memories"],
      color: "bg-sky-50",
      iconBg: "bg-sky-500",
    },
    {
      id: "marketing",
      name: "Prestigious Marketing",
      category: "Marketing",
      description: "A full-service marketing agency focused on helping businesses grow, connect, and build meaningful relationships.",
      link: "https://pmllc.org/",
      image: "https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg?auto=compress&cs=tinysrgb&w=800",
      icon: Megaphone,
      features: ["Brand Development", "Lead Generation", "Community Engagement"],
      color: "bg-purple-50",
      iconBg: "bg-purple-500",
    },
    {
      id: "technology",
      name: "Prestigious Apps",
      category: "Technology",
      description: "Developing smart, intuitive digital tools that simplify business management, client communications, and data organization.",
      link: null,
      image: "https://images.pexels.com/photos/4549831/pexels-photo-4549831.png?auto=compress&cs=tinysrgb&w=800",
      icon: Code,
      features: ["Smart Tools", "Business Solutions", "Data Management"],
      color: "bg-teal-50",
      iconBg: "bg-teal-500",
    },
    {
      id: "financial",
      name: "Easy Quick Fund",
      category: "Financial Services",
      description: "A financial services partner offering simple, flexible funding solutions for families and businesses.",
      link: "https://easyquickfund.com/",
      image: "https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?auto=compress&cs=tinysrgb&w=800",
      icon: DollarSign,
      features: ["100% Transparency", "24/7 Support", "Fast Approvals"],
      color: "bg-emerald-50",
      iconBg: "bg-emerald-500",
    },
    {
      id: "creditforward",
      name: "CreditForward360",
      category: "Financial Technology",
      description: "A modern credit education and financial wellness platform designed to help consumers understand, build, and strengthen their credit while providing tools that support long-term financial success.",
      link: "https://creditforward360.com",
      image: "https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800",
      icon: CreditCard,
      features: ["Credit Education", "Financial Wellness", "Credit Monitoring"],
      color: "bg-blue-50",
      iconBg: "bg-blue-600",
    },
    {
      id: "hub",
      name: "Prestigious Hub",
      category: "Operations Hub",
      description: "Central command center for operations, client management, training resources, and real-time business intelligence across all Prestigious Companies divisions.",
      link: "https://hub.prestigiouscompanies.org",
      image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=800",
      icon: LayoutDashboard,
      features: ["Client Management", "Training Resources", "Business Intelligence"],
      color: "bg-indigo-50",
      iconBg: "bg-indigo-500",
    },
    {
      id: "tv",
      name: "Prestigious TV",
      category: "Streaming Platform",
      description: "Access over 50,000 channels worldwide — live TV, movies, sports, and global entertainment all in one powerful streaming platform.",
      link: "https://Prestigious-TV.com",
      image: "https://images.pexels.com/photos/5721908/pexels-photo-5721908.jpeg?auto=compress&cs=tinysrgb&w=800",
      icon: Tv,
      features: ["50,000+ Channels", "Live Sports", "Global Entertainment"],
      color: "bg-red-50",
      iconBg: "bg-red-500",
      highlight: true,
    },
  ];

  return (
    <section
      id="divisions"
      className={`section-fade py-24 sm:py-32 bg-[#F8FAFC] ${isVisible ? "visible" : ""}`}
      data-testid="divisions-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 sm:mb-20">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/70 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" />
            Our Companies
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0A192F] tracking-tight font-['Outfit'] mb-6">
            Nine Divisions,{" "}
            <span className="text-[#D4AF37]">One Vision</span>
          </h2>
          <p className="text-lg text-[#475569] leading-relaxed">
            Each company operates independently while sharing one unified mission: empowering people, 
            businesses, and communities through innovative solutions. Together, our nine divisions form 
            an ecosystem spanning nonprofit outreach, travel, finance, technology, marketing, education, and business services.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {divisions.map((division, index) => (
            <div
              key={division.id}
              className={`division-card group animate-fade-in-up opacity-0 stagger-${Math.min(index + 1, 6)} ${division.highlight ? 'ring-2 ring-[#D4AF37] ring-offset-2' : ''}`}
              data-testid={`division-${division.id}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={division.image}
                  alt={division.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white ${division.iconBg}`}>
                    <division.icon className="w-3.5 h-3.5" />
                    {division.category}
                  </span>
                </div>

                {/* Highlight Badge for Featured Divisions */}
                {division.highlight && (
                  <div className="absolute top-4 right-4">
                    <span className="px-2 py-1 bg-[#D4AF37] text-[#0A192F] text-[10px] font-bold uppercase tracking-wider rounded">
                      Featured
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit'] mb-3 group-hover:text-[#D4AF37] transition-colors">
                  {division.name}
                </h3>
                <p className="text-[#475569] text-sm leading-relaxed mb-4">
                  {division.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {division.features.map((feature) => (
                    <span
                      key={feature}
                      className="px-3 py-1 bg-[#F8FAFC] text-xs font-medium text-[#0A192F] rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Link */}
                {division.link ? (
                  <a
                    href={division.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
                      division.highlight 
                        ? 'px-4 py-2 bg-[#0A192F] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#0A192F]' 
                        : 'text-[#0A192F] hover:text-[#D4AF37]'
                    }`}
                    data-testid={`division-link-${division.id}`}
                  >
                    {division.highlight ? 'Start Streaming' : 'Visit Website'}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-[#475569]">
                    Coming Soon
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DivisionsSection;
